import { stringToBase64 } from "../util/Base64";
import Settings from "../util/Settings";
import Time from "../util/Time";
import type { ReservationData } from "../util/useReservationState";
import { type Availability, type AvailabilityWithPage, type Course, type Group, type PostReserveResponse, type ReservationDetailData, Staff, type StatusData, type Store, type StoreForReservation } from "./ApiData";

type HttpMethod = "GET" | "POST" | "PUT" | "DELETE" | "PATCH";

export default class Api {
  readonly domain;
  constructor(domain: string) {
    this.domain = domain;
  }
  private async request<T>(method: HttpMethod, path: string, body?: any) {
    const { protocol, hostname, port } = window.location;
    const apiHost = hostname.replace(/^reserve\./, `api.`);
    const portSuffix = port ? `:${port}` : "";
    const uri = `${protocol}//${apiHost}${portSuffix}/v1/app/${this.domain}${path}`;
    let bodyString: string | undefined;
    if (body) {
      bodyString = JSON.stringify(body);
    }
    console.log(bodyString);

    const response = await fetch(uri, { body: bodyString, headers: { "Content-Type": "application/json" }, method });

    if (response.ok && 200 <= response.status && response.status < 300) {
      return (await response.json()) as T;
    } else {
      const errorData = await response.json();
      alert(`エラーが発生しました。\n${errorData.error}`);
      return null;
    }
  }

  readonly GetGroup = () => this.request<Group>("GET", "/group");

  readonly GetStores = () => this.request<Store[]>("POST", "/get-stores");
  readonly GetCourses = (storeId: number) => this.request<Course[]>("POST", "/get-courses-by-store-id", { storeId });
  readonly GetStoresForReservation = (storeIds: number[] | undefined, courseIds: number[] | undefined) => this.request<StoreForReservation[]>("POST", "/get-stores-for-reservation", { courseIds, storeIds });

  async GetAvailability(paging: number, store: Store, staff_id: number | undefined, treatment_course_id: number): Promise<Availability | null> {
    const date = Time.Now().Today() + paging * Settings.DateUnitCount * Time.ADay;
    return this.request<Availability>("POST", "/get-avaliability-by-date", { date, date_length: Settings.DateUnitCount, staff_id, store_id: store.id, treatment_course_id });
  }

  async GetNearestAvailability(store: Store, course: Course): Promise<AvailabilityWithPage | null> {
    const date = Time.Now().Today();
    return this.request<AvailabilityWithPage>("POST", "/get-nearest-avaliability-by-date", { course_duration: course.duration, date, date_length: Settings.DateUnitCount, store_id: store.id });
  }

  PostReserve(resvData: ReservationData, eventId: string, searchParams: URLSearchParams): Promise<PostReserveResponse | null> {
    if (!resvData.store || !resvData.course || !resvData.date) {
      return Promise.resolve(null);
    }

    return this.request<PostReserveResponse>("PUT", "/put-customer-resv", {
      // Facebookなどの広告媒体からCVした際のパラメータ解析用にサーバー側にSPAのクエリ文字列を送る
      analytics_queries: stringToBase64(searchParams.toString()),
      event_id: eventId,
      eventSourceUrl: window.location.href,
      fbc: resvData.fbc,
      fbp: resvData.fbp,
      group_id: resvData.group.id,
      mail: resvData.mail,
      name: resvData.name,
      phone: resvData.phone,
      request: resvData.request,
      rwg_token: resvData.rwg_token,
      staff_id: resvData.staff?.id,
      start_at: resvData.date,
      store_id: resvData.store.id,
      treatment_course_id: resvData.course.id,
      utm_campaign: resvData.utmParams.utmCampaign,
      utm_content: resvData.utmParams.utmContent,
      utm_medium: resvData.utmParams.utmMedium,
      utm_source: resvData.utmParams.utmSource,
      utm_term: resvData.utmParams.utmTerm,
    });
  }

  getReservationData = (rid: string) => {
    return this.request<ReservationDetailData>("POST", "/reservation-detail", { rid });
  };

  deleteReservation = (rid: string) => {
    return this.request<StatusData>("DELETE", "/reservation-detail", { rid });
  };

  getFiles = async (uuids: string[]) => {
    const signedUris = await this.request<{ uuid: string; uri: string }[]>("POST", "/generate-download-uris", { uuids });
    if (!signedUris) {
      return null;
    }

    const blobs = await Promise.all(
      signedUris.map(({ uuid, uri }) => {
        return fetch(uri)
          .then((res) => res.blob())
          .then((blob) => ({ blob, uuid }));
      }),
    );
    return blobs;
  };
}
